import React, { useState } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import PageHeader from './components/PageHeader';
import { toArabicNumerals } from './utils/arabicNumbers';
import { 
  FaQuestionCircle, 
  FaSearch, 
  FaBook, 
  FaEnvelope, 
  FaPhone, 
  FaChevronDown, 
  FaChevronUp,
  FaUser,
  FaCalendarAlt,
  FaCreditCard,
  FaVideo,
  FaComments,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle
} from 'react-icons/fa';

export default function HelpCenter() {
  const { isArabic, sidebarVisible, setSidebarVisible, handleLanguageChange } = useOutletContext() || {};
  const [sidebarOpen] = React.useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaqs, setExpandedFaqs] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('all');
  const navigate = useNavigate();
  const isMobile = window.innerWidth <= 600;

  // Get user role from localStorage
  const userRole = localStorage.getItem('userRole') || 'student';

  const categories = [
    { id: 'all', label: isArabic ? 'الكل' : 'All', icon: <FaBook /> },
    { id: 'account', label: isArabic ? 'الحساب' : 'Account', icon: <FaUser /> },
    { id: 'booking', label: isArabic ? 'الحجوزات' : 'Bookings', icon: <FaCalendarAlt /> },
    { id: 'payment', label: isArabic ? 'المدفوعات' : 'Payments', icon: <FaCreditCard /> },
    { id: 'sessions', label: isArabic ? 'الجلسات' : 'Sessions', icon: <FaVideo /> },
    { id: 'technical', label: isArabic ? 'تقني' : 'Technical', icon: <FaExclamationTriangle /> }
  ];

  const faqs = [
    {
      id: 1,
      category: 'account',
      question: isArabic ? 'كيف يمكنني إنشاء حساب جديد؟' : 'How do I create a new account?',
      answer: isArabic 
        ? 'يمكنك إنشاء حساب جديد من خلال النقر على زر "Sign Up" في الصفحة الرئيسية. ستحتاج إلى إدخال بريدك الإلكتروني وكلمة المرور والمعلومات الأساسية. بعد التسجيل، ستتلقى رسالة تأكيد عبر البريد الإلكتروني.'
        : 'You can create a new account by clicking the "Sign Up" button on the homepage. You\'ll need to enter your email, password, and basic information. After signing up, you\'ll receive a confirmation email.'
    },
    {
      id: 2,
      category: 'account',
      question: isArabic ? 'كيف يمكنني إعادة تعيين كلمة المرور؟' : 'How do I reset my password?',
      answer: isArabic
        ? 'إذا نسيت كلمة المرور الخاصة بك، انقر على "Forgot Password" في صفحة تسجيل الدخول. أدخل بريدك الإلكتروني وسنرسل لك رابط لإعادة تعيين كلمة المرور.'
        : 'If you forgot your password, click "Forgot Password" on the sign-in page. Enter your email and we\'ll send you a link to reset your password.'
    },
    {
      id: 3,
      category: 'booking',
      question: isArabic ? 'كيف يمكنني حجز جلسة مع معلم؟' : 'How do I book a session with a teacher?',
      answer: isArabic
        ? 'تصفح المعلمين المتاحين، اختر المعلم المناسب، ثم انقر على "Book Now". اختر التاريخ والوقت المناسبين، وأكمل تفاصيل الحجز. بعد ذلك، سيتم إرسال طلب الحجز للمعلم للموافقة عليه.'
        : 'Browse available teachers, select the teacher you want, then click "Book Now". Choose your preferred date and time, and complete the booking details. The booking request will then be sent to the teacher for approval.'
    },
    {
      id: 4,
      category: 'booking',
      question: isArabic ? 'كيف يمكنني إلغاء حجز؟' : 'How can I cancel a booking?',
      answer: isArabic
        ? 'يمكنك إلغاء الحجز من صفحة "Requests" في لوحة التحكم. انقر على الحجز الذي تريد إلغاءه واختر "Cancel". يرجى ملاحظة أن سياسة الإلغاء قد تختلف حسب المعلم.'
        : 'You can cancel a booking from the "Requests" page in your dashboard. Click on the booking you want to cancel and select "Cancel". Please note that cancellation policies may vary by teacher.'
    },
    {
      id: 5,
      category: 'payment',
      question: isArabic ? 'ما هي طرق الدفع المتاحة؟' : 'What payment methods are available?',
      answer: isArabic
        ? 'نستخدم Tap للدفع. نقبل الدفع عبر البطاقات الائتمانية والخصم. جميع المعاملات آمنة ومشفرة. يمكنك إضافة طريقة الدفع في صفحة "Profile" تحت قسم "Payment Methods".'
        : 'We use Tap for payments. We accept credit and debit cards. All transactions are secure and encrypted. You can add a payment method in the "Profile" page under the "Payment Methods" section.'
    },
    {
      id: 6,
      category: 'payment',
      question: isArabic ? 'متى يتم خصم المبلغ من حسابي؟' : 'When is the amount deducted from my account?',
      answer: isArabic
        ? 'يتم خصم المبلغ بعد موافقة المعلم على طلب الحجز. ستحصل على إشعار عبر البريد الإلكتروني عند اكتمال الدفع.'
        : 'The amount is deducted after the teacher approves your booking request. You\'ll receive an email notification when the payment is completed.'
    },
    {
      id: 7,
      category: 'sessions',
      question: isArabic ? 'كيف يمكنني الانضمام إلى جلسة أونلاين؟' : 'How do I join an online session?',
      answer: isArabic
        ? 'بعد موافقة المعلم على الحجز، ستتلقى رابط الجلسة عبر البريد الإلكتروني. يمكنك أيضاً العثور على الرابط في صفحة "Calendar" قبل موعد الجلسة. انقر على الرابط للانضمام إلى الجلسة.'
        : 'After the teacher approves your booking, you\'ll receive a session link via email. You can also find the link in the "Calendar" page before the session time. Click the link to join the session.'
    },
    {
      id: 8,
      category: 'sessions',
      question: isArabic ? 'ماذا أفعل إذا واجهت مشكلة تقنية أثناء الجلسة؟' : 'What should I do if I experience technical issues during a session?',
      answer: isArabic
        ? 'إذا واجهت مشاكل تقنية، حاول تحديث الصفحة أو إعادة الاتصال. إذا استمرت المشكلة، اتصل بالدعم الفني على الفور. يمكننا أيضاً إعادة جدولة الجلسة إذا لزم الأمر.'
        : 'If you experience technical issues, try refreshing the page or reconnecting. If the problem persists, contact technical support immediately. We can also reschedule the session if necessary.'
    },
    {
      id: 9,
      category: 'technical',
      question: isArabic ? 'لماذا لا يعمل الموقع بشكل صحيح؟' : 'Why is the website not working properly?',
      answer: isArabic
        ? 'تأكد من أنك تستخدم أحدث إصدار من المتصفح. امسح ذاكرة التخزين المؤقت وملفات تعريف الارتباط، ثم أعد تحميل الصفحة. إذا استمرت المشكلة، اتصل بالدعم الفني.'
        : 'Make sure you\'re using the latest version of your browser. Clear your cache and cookies, then reload the page. If the problem persists, contact technical support.'
    },
    {
      id: 10,
      category: 'technical',
      question: isArabic ? 'كيف يمكنني تحديث معلومات ملفي الشخصي؟' : 'How can I update my profile information?',
      answer: isArabic
        ? 'انتقل إلى صفحة "Profile" من القائمة الجانبية. يمكنك تحديث معلوماتك الشخصية، صورة الملف الشخصي، وتفضيلاتك. لا تنسَ حفظ التغييرات بعد التعديل.'
        : 'Go to the "Profile" page from the sidebar menu. You can update your personal information, profile picture, and preferences. Don\'t forget to save your changes after editing.'
    }
  ];

  const filteredFaqs = faqs.filter(faq => {
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    const matchesSearch = !searchQuery || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const toggleFaq = (id) => {
    setExpandedFaqs(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <div style={{
      display: 'flex',
      minHeight: '100vh',
      background: '#f7f9fb',
      fontFamily: "'IBM Plex Sans Arabic', sans-serif"
    }}>
      <Sidebar
        isOpen={sidebarOpen}
        isVisible={sidebarVisible}
        setSidebarVisible={setSidebarVisible}
        userRole={userRole}
        isArabic={isArabic}
        onLanguageChange={handleLanguageChange}
      />
      
      <main style={{
        flex: 1,
        padding: isMobile ? '20px 16px' : '40px 48px',
        paddingLeft: sidebarVisible ? (isMobile ? '16px' : '72px') : (isMobile ? '16px' : '48px'),
        transition: 'padding-left 0.3s',
        width: '100%'
      }}
      dir={isArabic ? 'rtl' : 'ltr'}
      >
        <PageHeader 
          title={isArabic ? 'مركز المساعدة' : 'Help Center'} 
          subtitle={isArabic ? 'ابحث عن إجابات لأسئلتك أو اتصل بنا' : 'Find answers to your questions or contact us'} 
          isArabic={isArabic} 
        />

        {/* Search Bar */}
        <div style={{
          marginBottom: 32,
          position: 'relative'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            background: '#fff',
            borderRadius: 16,
            padding: '0 20px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
            border: '2px solid #e5e7eb',
            transition: 'all 0.2s'
          }}
          onFocus={e => {
            e.currentTarget.style.borderColor = '#ff7a1a';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(255, 122, 26, 0.1)';
          }}
          onBlur={e => {
            e.currentTarget.style.borderColor = '#e5e7eb';
            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
          }}
          >
            <FaSearch size={20} color="#6b7280" style={isArabic ? { marginLeft: 12 } : { marginRight: 12 }} />
            <input
              type="text"
              placeholder={isArabic ? 'ابحث عن سؤال...' : 'Search for a question...'}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                padding: '16px 0',
                fontSize: 16,
                background: 'transparent',
                color: '#111827',
                fontFamily: 'inherit'
              }}
            />
          </div>
        </div>

        {/* Categories */}
        <div style={{
          display: 'flex',
          gap: 12,
          marginBottom: 32,
          overflowX: 'auto',
          paddingBottom: 8,
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}>
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '12px 20px',
                background: selectedCategory === category.id 
                  ? 'linear-gradient(135deg, #ff7a1a 0%, #ff9a4a 100%)' 
                  : '#fff',
                color: selectedCategory === category.id ? '#fff' : '#374151',
                border: selectedCategory === category.id ? 'none' : '2px solid #e5e7eb',
                borderRadius: 12,
                fontWeight: 700,
                fontSize: 14,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'all 0.2s',
                boxShadow: selectedCategory === category.id 
                  ? '0 4px 15px rgba(255, 122, 26, 0.3)' 
                  : '0 2px 6px rgba(0,0,0,0.04)'
              }}
              onMouseEnter={e => {
                if (selectedCategory !== category.id) {
                  e.currentTarget.style.borderColor = '#ff7a1a';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={e => {
                if (selectedCategory !== category.id) {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              {category.icon}
              {category.label}
            </button>
          ))}
        </div>

        {/* FAQs */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
          marginBottom: 40
        }}>
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map(faq => (
              <div
                key={faq.id}
                style={{
                  background: '#fff',
                  borderRadius: 16,
                  border: '2px solid #e5e7eb',
                  overflow: 'hidden',
                  transition: 'all 0.3s',
                  boxShadow: expandedFaqs[faq.id] 
                    ? '0 8px 24px rgba(0,0,0,0.1)' 
                    : '0 2px 8px rgba(0,0,0,0.05)'
                }}
                onMouseEnter={e => {
                  if (!expandedFaqs[faq.id]) {
                    e.currentTarget.style.borderColor = '#ff7a1a';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }
                }}
                onMouseLeave={e => {
                  if (!expandedFaqs[faq.id]) {
                    e.currentTarget.style.borderColor = '#e5e7eb';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }
                }}
              >
                <button
                  onClick={() => toggleFaq(faq.id)}
                  style={{
                    width: '100%',
                    padding: '20px 24px',
                    background: 'transparent',
                    border: 'none',
                    textAlign: isArabic ? 'right' : 'left',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 16
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <h3 style={{
                      fontSize: 16,
                      fontWeight: 700,
                      color: '#111827',
                      marginBottom: 4,
                      fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                    }}>
                      {faq.question}
                    </h3>
                  </div>
                  <div style={{
                    flexShrink: 0,
                    color: '#ff7a1a',
                    transition: 'transform 0.3s',
                    transform: expandedFaqs[faq.id] ? 'rotate(180deg)' : 'rotate(0deg)'
                  }}>
                    <FaChevronDown size={20} />
                  </div>
                </button>
                {expandedFaqs[faq.id] && (
                  <div style={{
                    padding: '0 24px 20px 24px',
                    color: '#6b7280',
                    fontSize: 15,
                    lineHeight: 1.6,
                    fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                    textAlign: isArabic ? 'right' : 'left'
                  }}>
                    {faq.answer}
                  </div>
                )}
              </div>
            ))
          ) : (
            <div style={{
              textAlign: 'center',
              padding: '60px 20px',
              background: '#fff',
              borderRadius: 16,
              border: '2px solid #e5e7eb'
            }}>
              <FaQuestionCircle size={48} color="#9ca3af" style={{ marginBottom: 16 }} />
              <h3 style={{
                fontSize: 20,
                fontWeight: 700,
                color: '#111827',
                marginBottom: 8
              }}>
                {isArabic ? 'لا توجد نتائج' : 'No results found'}
              </h3>
              <p style={{
                color: '#6b7280',
                fontSize: 15
              }}>
                {isArabic 
                  ? 'جرب البحث بكلمات مختلفة أو تصفح الفئات الأخرى'
                  : 'Try searching with different keywords or browse other categories'}
              </p>
            </div>
          )}
        </div>

        {/* Contact Section */}
        <div style={{
          background: 'linear-gradient(135deg, #fff 0%, #f8fafc 100%)',
          borderRadius: 24,
          padding: isMobile ? 24 : 40,
          boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
          border: '1px solid #e5e7eb'
        }}>
          <h2 style={{
            fontSize: isMobile ? 24 : 28,
            fontWeight: 800,
            color: '#111827',
            marginBottom: 8,
            textAlign: 'center',
            fontFamily: "'IBM Plex Sans Arabic', sans-serif"
          }}>
            {isArabic ? 'لا تزال بحاجة إلى المساعدة؟' : 'Still need help?'}
          </h2>
          <p style={{
            fontSize: 16,
            color: '#6b7280',
            textAlign: 'center',
            marginBottom: 32,
            fontFamily: "'IBM Plex Sans Arabic', sans-serif"
          }}>
            {isArabic
              ? 'اتصل بفريق الدعم لدينا وسنكون سعداء لمساعدتك'
              : 'Contact our support team and we\'ll be happy to help you'}
          </p>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(2, 1fr)',
            gap: 20
          }}>
            <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: 24,
              border: '2px solid #e5e7eb',
              textAlign: 'center',
              transition: 'all 0.3s'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = '#ff7a1a';
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            >
              <div style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <FaEnvelope size={24} color="#ff7a1a" />
              </div>
              <h3 style={{
                fontSize: 18,
                fontWeight: 700,
                color: '#111827',
                marginBottom: 8
              }}>
                {isArabic ? 'البريد الإلكتروني' : 'Email'}
              </h3>
              <a 
                href="mailto:support@taealam.com" 
                style={{
                  color: '#ff7a1a',
                  textDecoration: 'none',
                  fontSize: 15,
                  fontWeight: 600
                }}
              >
                support@taealam.com
              </a>
            </div>
            <div style={{
              background: '#fff',
              borderRadius: 16,
              padding: 24,
              border: '2px solid #e5e7eb',
              textAlign: 'center',
              transition: 'all 0.3s'
            }}
            onMouseEnter={e => {
              e.currentTarget.style.borderColor = '#ff7a1a';
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            >
              <div style={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px'
              }}>
                <FaPhone size={24} color="#ff7a1a" />
              </div>
              <h3 style={{
                fontSize: 18,
                fontWeight: 700,
                color: '#111827',
                marginBottom: 8
              }}>
                {isArabic ? 'الهاتف' : 'Phone'}
              </h3>
              <a 
                href="tel:+97312345678" 
                style={{
                  color: '#ff7a1a',
                  textDecoration: 'none',
                  fontSize: 15,
                  fontWeight: 600
                }}
              >
                {isArabic ? toArabicNumerals('+973 3903 0980', isArabic) : '+973 3903 0980'}
              </a>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

